import { DotpadSDK } from "@/util/dotpad/DotpadSDK.js";
import { Group, Rect, Line, Canvas, StaticCanvas } from "fabric";

const dotpadSDK = new DotpadSDK()
const NUMBER_OF_HORIZONTAL_PINS = 60
const NUMBER_OF_VERTICAL_PINS = 40

const _staticCanvasRegistry = new Map()

function getPixelArray(item) {
    const dotPadInfo = item.dotPadInfo
    dotpadSDK.setDotPadSDKInfo(dotPadInfo)

    const hex = item.graphic.data
    const byteArr = dotpadSDK.hexToBytes(hex)
    return _calculatePixelArray(byteArr, dotPadInfo)
}

// 캔버스 생성
function createCanvas(canvasId, canvasWidth, canvasHeight) {
    const canvas = new Canvas(canvasId)
    canvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight
    })

    return canvas
}

function createStaticCanvas(canvasId, canvasWidth, canvasHeight) {
    if (_staticCanvasRegistry.has(canvasId)) {
        _staticCanvasRegistry.get(canvasId).dispose()
        _staticCanvasRegistry.delete(canvasId)
    }
    const staticCanvas = new StaticCanvas(canvasId)
    staticCanvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight
    })
    _staticCanvasRegistry.set(canvasId, staticCanvas)
    return staticCanvas
}

// 캔버스에 그리드 그리기
function drawGridOnTheCanvas(canvasWidth, canvasHeight) {
    const gridLines = [];
    const gridOptions = {selectable: false, stroke: '#cccccc'};

    for (let row = 0; row < NUMBER_OF_VERTICAL_PINS; row++) {
        const y = (row / NUMBER_OF_VERTICAL_PINS) * canvasHeight;
        gridLines.push(new Line([0, y, canvasWidth, y], gridOptions));
    }

    for (let column = 0; column < NUMBER_OF_HORIZONTAL_PINS; column++) {
        const x = (column / NUMBER_OF_HORIZONTAL_PINS) * canvasWidth;
        gridLines.push(new Line([x, 0, x, canvasHeight], gridOptions));
    }

    return new Group(gridLines, {
        selectable: false,
        evented: false,
    });
}

function drawGridOnTheCanvasForChangeTest(params) {
    const dotPadInfo = params.dotPadInfo
    const horizontalPins = dotPadInfo ? dotPadInfo.width : NUMBER_OF_HORIZONTAL_PINS
    const verticalPins   = dotPadInfo ? dotPadInfo.height : NUMBER_OF_VERTICAL_PINS

    const canvasW = params.canvasWidth
    const canvasH = params.canvasHeight

    // 🔥 정사각형 비율 유지하여 스케일 계산
    const offsetX = 0
    const offsetY = 0
    const scale = Math.min(canvasW / horizontalPins, canvasH / verticalPins)
    const gridLines = []
    const gridOptions = { selectable: false, stroke: '#cccccc' }

    // 🔥 수평 라인
    for (let row = 0; row <= verticalPins; row++) {
        const y = offsetY + row * scale
        gridLines.push(new Line([offsetX, y, offsetX + horizontalPins * scale, y], gridOptions))
    }

    // 🔥 수직 라인
    for (let col = 0; col <= horizontalPins; col++) {
        const x = offsetX + col * scale
        gridLines.push(new Line([x, offsetY, x, offsetY + verticalPins * scale], gridOptions))
    }

    return new Group(gridLines, {
        selectable: false,
        evented: false,
    })
}

// 캔버스에 촉각 정보 그리기
function drawTactileDataOnTheCanvas(pixelArr, canvasWidth, canvasHeight) {
    const pinW = canvasWidth / NUMBER_OF_HORIZONTAL_PINS;
    const pinH = canvasHeight / NUMBER_OF_VERTICAL_PINS;
    const array = [];
    let index = 0;

    for (let i = 0; i < NUMBER_OF_VERTICAL_PINS; i++) {
        for (let j = 0; j < NUMBER_OF_HORIZONTAL_PINS; j++) {
            if (pixelArr[index]) {
                const pin = new Rect({
                    fill: 'black',
                    left: pinW * j,
                    top: pinH * i,
                    width: pinW,
                    height: pinH,
                    hasBorders: false,
                    hasControls: false,
                    hasRotatingPoint: false,
                    lockMovementX: true,
                    lockMovementY: true,
                    selectable: false,
                    hoverCursor: "default",
                });
                array.push(pin);
            }
            index++;
        }
    }

    return new Group(array);
}

function drawTactileDataOnTheCanvasForChangeTest(params) {
    const dotPadInfo = params.dotPadInfo
    const pixelArr = params.pixelArr || []

    let horizontalPins = NUMBER_OF_HORIZONTAL_PINS
    let verticalPins = NUMBER_OF_VERTICAL_PINS
    let deviceKind = '320'

    if (dotPadInfo) {
        // 8로 나누어 떨어지지 않는 페이지 대응
        let { width, height } = dotPadInfo
        if (width % 2 !== 0 || height % 4 !== 0) {
            width = Math.ceil(width / 2) * 2
            height = Math.ceil(height / 4) * 4
        }

        horizontalPins = width
        verticalPins = height
        deviceKind = dotPadInfo.deviceKind
    }

    const canvasW = params.canvasWidth
    const canvasH = params.canvasHeight

    const scale = Math.min(canvasW / horizontalPins, canvasH / verticalPins)

    const offsetX = (canvasW - horizontalPins * scale) / 2
    const offsetY = (canvasH - verticalPins * scale) / 2

    const array = []

    let index = 0
    for (let i = 0; i < verticalPins; i++) {
        for (let j = 0; j < horizontalPins; j++) {
            if (pixelArr[index]) {
                array.push(new Rect({
                    fill: 'black',
                    left: offsetX + scale * j,
                    top:  offsetY + scale * i,
                    width: scale,
                    height: scale,
                    selectable: false,
                    hasBorders: false,
                    hasControls: false,
                    lockMovementX: true,
                    lockMovementY: true,
                }))
            }
            index++
        }
    }
    return new Group(array)
}

function drawThumbnail(ctx, pixelArr, dotPadInfo, canvasW, canvasH) {
    let horizontalPins = NUMBER_OF_HORIZONTAL_PINS
    let verticalPins = NUMBER_OF_VERTICAL_PINS

    if (dotPadInfo) {
        let { width, height } = dotPadInfo
        if (width % 2 !== 0 || height % 4 !== 0) {
            width = Math.ceil(width / 2) * 2
            height = Math.ceil(height / 4) * 4
        }
        horizontalPins = width
        verticalPins = height
    }

    const scale = Math.min(canvasW / horizontalPins, canvasH / verticalPins)
    const offsetX = (canvasW - horizontalPins * scale) / 2
    const offsetY = (canvasH - verticalPins * scale) / 2

    ctx.clearRect(0, 0, canvasW, canvasH)
    ctx.fillStyle = 'black'

    let index = 0
    for (let i = 0; i < verticalPins; i++) {
        for (let j = 0; j < horizontalPins; j++) {
            if (pixelArr[index]) {
                ctx.fillRect(offsetX + scale * j, offsetY + scale * i, scale, scale)
            }
            index++
        }
    }
}

function getVoxel() {
  return {
    NUMBER_OF_HORIZONTAL_PINS,
    NUMBER_OF_VERTICAL_PINS,
  }
}

function _calculatePixelArray(byteArr, dotPadInfo) {
    const result = [];
    for (let i = 0; i < byteArr.length; i++) {
        const start_index = Math.floor(i / dotPadInfo.halfWidth) * dotPadInfo.width * 4 + (i % dotPadInfo.halfWidth) * 2;
        result[start_index] = !!(byteArr[i] & (0x01 << 0));
        result[start_index + dotPadInfo.width] = !!(byteArr[i] & (0x01 << 1));
        result[start_index + dotPadInfo.doubleWidth] = !!(byteArr[i] & (0x01 << 2));
        result[start_index + dotPadInfo.tripleWidth] = !!(byteArr[i] & (0x01 << 3));
        result[start_index + 1] = !!(byteArr[i] & (0x01 << 4));
        result[start_index + dotPadInfo.width + 1] = !!(byteArr[i] & (0x01 << 5));
        result[start_index + dotPadInfo.doubleWidth + 1] = !!(byteArr[i] & (0x01 << 6));
        result[start_index + dotPadInfo.tripleWidth + 1] = !!(byteArr[i] & (0x01 << 7));
    }
    return result;
}

export default {
    createCanvas,
    createStaticCanvas,
    drawGridOnTheCanvas,
    drawGridOnTheCanvasForChangeTest,
    drawTactileDataOnTheCanvas,
    drawTactileDataOnTheCanvasForChangeTest,
    drawThumbnail,
    getPixelArray,
    getVoxel
}
